import { useEffect, useState, useRef, useCallback } from 'react';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import NavInf from '../../components/NavInf/NavInf';
import ProductWrapper from '../../components/Product/ProductWrapper';
import Masonry from 'react-masonry-css';
import { ClipLoader } from 'react-spinners';
import throttle from 'lodash.throttle';
import './Favorites.css';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { secureFetch } from '../../utils/secureFetch';

const ITEMS_PER_LOAD = 10;

function Favorites() {
  const [products, setProducts] = useState([]);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadIndexRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);

  // Configuración de breakpoints para Masonry
  const breakpointColumnsObj = {
    default: 6,
    1600: 6,
    1200: 5,
    768: 3,
    480: 2
  };

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    setTimeout(() => {
      const next = products.slice(
        loadIndexRef.current,
        loadIndexRef.current + ITEMS_PER_LOAD
      );
      setVisibleProducts(prev => [...prev, ...next]);
      loadIndexRef.current += ITEMS_PER_LOAD;
      if (loadIndexRef.current >= products.length) {
        setHasMore(false);
      }
      setLoadingMore(false);
    }, 500);
  }, [products, loadingMore, hasMore]);

  const handleScroll = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;

    if (
      window.innerHeight +
      document.documentElement.scrollTop +
      200 >=
      document.documentElement.offsetHeight
    ) {
      loadMore();
    }
  }, [loading, loadingMore, hasMore, loadMore]);

  useEffect(() => {
    const fetchFavoritos = async () => {
      try {
        setLoading(true);
        setVisibleProducts([]);
        loadIndexRef.current = 0;
        setHasMore(true);

        const userId = JSON.parse(localStorage.getItem('usuario'))?.id;
        if (!userId) return;

        const res = await secureFetch(`https://api.minymol.com/favorites/${userId}`);
        const data = await res.json();

        if (!Array.isArray(data)) throw new Error('Respuesta inválida de favoritos');

        const ids = data.map(fav => fav.id).filter(Boolean);
        if (!ids.length) {
          setProducts([]);
          return;
        }

        const previewsRes = await fetch('https://api.minymol.com/products/previews', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids })
        });

        const previews = await previewsRes.json();
        setProducts(previews);
      } catch (error) {
        console.error('Error al cargar favoritos:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchFavoritos();
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!loading && products.length > 0) {
      const inicial = products.slice(0, ITEMS_PER_LOAD);
      setVisibleProducts(inicial);
      loadIndexRef.current = ITEMS_PER_LOAD;
      setHasMore(products.length > ITEMS_PER_LOAD);
    }
  }, [loading, products]);

  useEffect(() => {
    const throttledScroll = throttle(handleScroll, 200);

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      throttledScroll.cancel();
    };
  }, [handleScroll]);

  return (
    <div className="favorites-container">
      <Header />
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh', width: '100%' }}>
          <ClipLoader color="#fa7e17" size={30} />
        </div>
      ) : (
        <>
          {visibleProducts.length > 0 ? (
            <Masonry
              breakpointCols={breakpointColumnsObj}
              className="products-cont masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {visibleProducts.map((prod, index) => (
                <ProductWrapper key={`wrapper-${index}`} product={prod} index={index} />
              ))}
            </Masonry>
          ) : (
            <p style={{ padding: '1rem', textAlign: 'center' }}>
              No tienes productos favoritos aún.
            </p>
          )}
          {loadingMore && hasMore && (
            <div className="loader-bottom" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh', width: '100%' }}>
              <ClipLoader color="#fa7e17" size={25} />
            </div>
          )}
          {!hasMore && visibleProducts.length > 0 && (
            <div style={{ textAlign: 'center', color: '#888', padding: '1rem' }}>
              No hay más productos para mostrar
            </div>
          )}
        </>
      )}
      <NavInf selected={"favorites"} />
      <Footer />
    </div>
  );
}

export default Favorites;
